import React, { useRef, useEffect, useState } from 'react';
import './AudioPlayer.css';

interface AudioPlayerProps {
  audioUrl: string;
  timeStart?: string; // Formato "MM:SS" ou "M:SS"
  title?: string;
  onLoadError?: (error: string) => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  timeStart,
  title = 'Reprodutor de Áudio',
  onLoadError
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSetInitialTime, setHasSetInitialTime] = useState(false);

  // Converter tempo MM:SS para segundos
  const timeToSeconds = (timeString: string): number => {
    if (!timeString) return 0;
    
    const parts = timeString.split(':');
    if (parts.length !== 2) return 0;
    
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    
    if (isNaN(minutes) || isNaN(seconds)) return 0;
    
    return minutes * 60 + seconds;
  };

  // Converter segundos para formato MM:SS
  const secondsToTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calcular tempo inicial em segundos
  const initialTimeSeconds = timeStart ? timeToSeconds(timeStart) : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      console.log('🎵 Áudio carregado - Duração:', audio.duration);
      setDuration(audio.duration);
      setIsLoading(false);
      
      // Definir tempo inicial se especificado e ainda não foi definido
      if (initialTimeSeconds > 0 && !hasSetInitialTime && audio.duration > initialTimeSeconds) {
        console.log(`⏰ Definindo tempo inicial: ${timeStart} (${initialTimeSeconds}s)`);
        audio.currentTime = initialTimeSeconds;
        setCurrentTime(initialTimeSeconds);
        setHasSetInitialTime(true);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleError = (e: Event) => {
      const errorMsg = 'Erro ao carregar o arquivo de áudio';
      console.error('❌ Erro no áudio:', e);
      setError(errorMsg);
      setIsLoading(false);
      onLoadError?.(errorMsg);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    // Adicionar event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    // Cleanup
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [audioUrl, initialTimeSeconds, timeStart, hasSetInitialTime, onLoadError]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      // Se ainda não definiu o tempo inicial e há um timeStart
      if (initialTimeSeconds > 0 && !hasSetInitialTime) {
        audio.currentTime = initialTimeSeconds;
        setHasSetInitialTime(true);
      }
      audio.play().catch(err => {
        console.error('Erro ao reproduzir áudio:', err);
        setError('Erro ao reproduzir áudio');
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const jumpToStart = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const targetTime = initialTimeSeconds > 0 ? initialTimeSeconds : 0;
    audio.currentTime = targetTime;
    setCurrentTime(targetTime);
  };

  if (error) {
    return (
      <div className="audio-player error">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-player">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
      />
      
      <div className="audio-player-header">
        <h3 className="audio-title">{title}</h3>
        {timeStart && (
          <div className="time-info">
            <span className="time-badge">
              ⏰ Início: {timeStart}
            </span>
          </div>
        )}
      </div>

      <div className="audio-controls">
        <button
          className="play-pause-btn"
          onClick={togglePlayPause}
          disabled={isLoading}
          title={isPlaying ? 'Pausar' : 'Reproduzir'}
        >
          {isLoading ? (
            <span className="loading-spinner">⏳</span>
          ) : isPlaying ? (
            <span className="pause-icon">⏸️</span>
          ) : (
            <span className="play-icon">▶️</span>
          )}
        </button>

        {initialTimeSeconds > 0 && (
          <button
            className="jump-start-btn"
            onClick={jumpToStart}
            disabled={isLoading}
            title={`Pular para ${timeStart}`}
          >
            <span>⏭️ {timeStart}</span>
          </button>
        )}

        <div className="time-display">
          <span className="current-time">{secondsToTime(currentTime)}</span>
          <span className="separator">/</span>
          <span className="total-time">{secondsToTime(duration)}</span>
        </div>
      </div>

      <div className="progress-container">
        <input
          type="range"
          className="progress-bar"
          min="0"
          max={duration || 0}
          value={currentTime}
          onChange={handleSeek}
          disabled={isLoading}
        />
        
        {/* Indicador visual para timeStart */}
        {duration > 0 && initialTimeSeconds > 0 && (
          <div className="time-markers">
            <div
              className="time-marker start-marker"
              style={{ left: `${(initialTimeSeconds / duration) * 100}%` }}
              title={`Início: ${timeStart}`}
            />
          </div>
        )}
      </div>

      {isLoading && (
        <div className="loading-message">
          <span>Carregando áudio...</span>
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
